import axios from 'axios';

const BASE_URL = '/api/resellerclub/api';

/**
 * Check domain availability using ResellerClub API
 * @param {string} domainName - The base domain name (e.g., 'cplus')
 * @param {string[]} tlds - Array of TLDs to check (e.g., ['com', 'net'])
 * @returns {Promise<Object>} - Map of domain to availability status
 */
export const checkAvailability = async (domainName, tlds = ['com', 'net', 'org']) => {
    try {
        // Clean domain name from any TLDs just in case
        const cleanName = domainName.split('.')[0];

        // Construct query parameters
        const params = new URLSearchParams();
        params.append('domain-name', cleanName);
        tlds.forEach(tld => {
            // ResellerClub expects TLDs without the leading dot
            params.append('tlds', tld.replace('.', ''));
        });

        const response = await axios.get(`${BASE_URL}/domains/available.json`, { params });

        // Normalizing response: ResellerClub returns { "domain.com": { status: "available" }, ... }
        // We normalize this to a simpler format if needed, but the raw format is fine too.
        return response.data;
    } catch (error) {
        console.error('ResellerClub Availability API Error:', error);
        // Return empty object on error to prevent UI crash
        return {};
    }
};

/**
 * Mappings between our TLD format and ResellerClub pricing keys
 */
const TLD_MAPPING = {
    '.com': 'domcom',
    '.net': 'domnet',
    '.org': 'domorg',
    '.io': 'domio',
    '.co': 'domco',
    '.ai': 'domai',
    '.dev': 'domdev',
    '.app': 'domapp',
    '.biz': 'dombiz',
    '.info': 'dominfo',
    '.tech': 'domtech',
    '.online': 'domonline',
    '.store': 'domstore'
};

/**
 * Get the customer-specific pricing for common TLDs
 * Returns a simplified object: { '.com': 12.99, '.net': 11.99, ... }
 */
export const getTLDPricing = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/products/customer-price.json`);
        const rawData = response.data;
        const prices = {};

        Object.entries(TLD_MAPPING).forEach(([tld, rcKey]) => {
            // ResellerClub path: productKey -> addnewdomain -> 1 (year)
            const priceData = rawData[rcKey]?.addnewdomain?.['1'];
            if (priceData) {
                prices[tld] = parseFloat(priceData);
            }
        });

        return prices;
    } catch (error) {
        console.error('ResellerClub Pricing API Error:', error);
        return null;
    }
};
